/*
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import { IView, IViewInfo } from "../../../view/IView";
import { IEditor } from "../../../IEditor";
import { LayoutState } from "../../../ui/components/Layout/LayoutState";
import { ReferenceGuideLayoutConfig } from "./Layout";
import { RuntimeService } from "../../../runtime/RuntimeService";
import { IntegrationService } from "../../../integration/IntegrationService";
import { IIntegrationType } from "../../../integration/IntegrationServiceTypes";
import { ComponentCategories } from "../../../common/ComponentCategories";
import { mapperChild } from "./mappers/mapperChild";
import { getTypeFromSchema } from "./helpers/getTypeFromSchema";
import { createEventEmitter, emitEvent, isArray } from "@adapptio/lib-shared";

import {
	REFERENCE_CHILD_TYPE,
	REFERENCE_TYPE,
	ReferenceAction,
	ReferenceComponent,
	ReferenceDataSource,
	ReferenceEvent,
	ReferenceFunction,
	ReferenceIntegration,
	TReferenceData
} from "./ReferenceGuideTypes";

import {
	BlueprintAction,
	BlueprintActionNodeTypes,
	CoreComponentEventNodeTypes,
	IComponentDefRegistry,
	IDataSourceDefRegistry,
	IFunctionDefRegistry
} from "@adapptio/lib-core";

interface IReferenceGuideParams {
	initSelected?: any;
}

export class ReferenceGuide implements IView<IReferenceGuideParams> {
	private runtimeService: RuntimeService;
	private integrationService: IntegrationService;

	private nav: TReferenceData[];
	private components: ReferenceComponent;
	private functions: ReferenceFunction;
	private integrations: ReferenceIntegration;
	private actions: ReferenceAction;
	private events: ReferenceEvent;
	private dataSource: ReferenceDataSource;

	private content: TReferenceData;

	private searchComponents: ReferenceComponent;
	private searchFunctions: ReferenceFunction;
	private searchIntegrations: ReferenceIntegration;
	private searchActions: ReferenceAction;
	private searchEvents: ReferenceEvent;
	private searchDataSource: ReferenceDataSource;

	public selectNav: TReferenceData;

	public onSelectedChange = createEventEmitter<void>();

	private layout = new LayoutState(ReferenceGuideLayoutConfig);

	public constructor(private dc: IEditor) {
		this.runtimeService = dc.get("runtimeService");
		this.integrationService = dc.get("integrationService");
	}

	public async init(params: IReferenceGuideParams): Promise<void> {
		this.components = this.getComponentsNav();
		this.functions = this.getFunctionsNav();
		this.integrations = this.getIntegrationsNav();
		this.actions = this.getActionsNav();
		this.events = this.getEventsNav();
		this.dataSource = this.getDataSourceNav();

		this.searchComponents = this.components;
		this.searchFunctions = this.functions;
		this.searchIntegrations = this.integrations;
		this.searchActions = this.actions;
		this.searchEvents = this.events;
		this.searchDataSource = this.dataSource;

		if (params?.initSelected) {
			this.selectNav = params.initSelected;
		} else {
			this.selectNav = this.components?.child?.[0]?.child?.[0];
		}

		this.combineNav();
		this.updateContent(this.selectNav);

		emitEvent(this.onSelectedChange);
	}

	public getSelected() {
		return this.selectNav;
	}

	private getComponent(name) {
		const registredComponents: IComponentDefRegistry = this.runtimeService.getComponentRegistry();
		const componentData = registredComponents.get(name);

		if (!componentData) {
			return null;
		}

		const component: ReferenceComponent = {
			label: componentData.label || null,
			description: componentData.description || null,
			childType: REFERENCE_CHILD_TYPE.PROPERTIES,
			type: REFERENCE_TYPE.COMPONETS,
			hidden: componentData?.hidden || false,
			category: REFERENCE_TYPE.COMPONETS,
			child: Object.keys(componentData.props).map((key) => mapperChild(componentData.props[key].schema))
		};

		return component;
	}

	private getDataSource(name) {
		const registredDataSource: IDataSourceDefRegistry = this.runtimeService.getDataSourceRegistry();
		const data = registredDataSource["items"][name];

		if (!data) {
			return null;
		}

		const component: ReferenceDataSource = {
			label: data.label,
			description: data.description,
			type: REFERENCE_TYPE.DATASOURCE,
			name: data.name,
			category: REFERENCE_TYPE.DATASOURCE,
			child: Object.values(data.opts).map((item) => mapperChild(item.schema))
		};

		return component;
	}

	private getFunction(name: string) {
		const registredFunctions: IFunctionDefRegistry = this.runtimeService.getFunctionRegistry();
		const data = registredFunctions.get(name);

		if (!data) {
			return null;
		}

		const component: ReferenceFunction = {
			label: data.label,
			description: data.description,
			name: data.name,
			category: REFERENCE_TYPE.FUNCTIONS,
			type: REFERENCE_TYPE.FUNCTIONS,
			childType: REFERENCE_CHILD_TYPE.ARGUMENTS,
			returnType: "any",
			child: data.argSchemas.map((itemArgs) => mapperChild(itemArgs)),
			argsRest: {
				type: getTypeFromSchema(data.argRestSchema?.["constSchema"])
			}
		};

		return component;
	}

	private getIntegration(name: string) {
		const data: IIntegrationType = this.integrationService.getIntegrationType(name);

		if (!data) {
			return null;
		}

		const component: ReferenceIntegration = {
			label: data.label,
			category: REFERENCE_TYPE.INTEGRATIONS,
			hidden: data.hidden,
			description: data.description,
			child: mapperChild(data.optionsSchema)
		};

		return component;
	}

	private getIntegrationFunction(type: string, name: string, parentLabel: string) {
		const data = this.integrationService.getSingleIntegrationFunction(type, name);

		if (!data) {
			return null;
		}

		const component: ReferenceIntegration = {
			category: parentLabel,
			label: data.label,
			type: REFERENCE_TYPE.INTEGRATIONS,
			childType: REFERENCE_CHILD_TYPE.ARGUMENTS,
			name: data.functionName,
			description: data.description,
			child: this.sort(mapperChild(data.paramsSchema))
		};

		return component;
	}

	private getAction(name: string): ReferenceAction {
		if (name === "ActionConfig") {
			const data = BlueprintAction;

			if (!data) {
				return null;
			}

			const component: ReferenceAction = {
				label: "Settings",
				description: "This root setting actions",
				type: REFERENCE_TYPE.ACTIONS,
				category: REFERENCE_TYPE.ACTIONS,
				child: Object.keys(data.opts.props)
					.map((key) => mapperChild(data.opts.props[key].schema))
					.filter((item) => item !== undefined && !isArray(item))
			};

			return component;
		} else {
			const data = BlueprintActionNodeTypes[name];

			if (!data) {
				return null;
			}

			const component: ReferenceAction = {
				label: data.label,
				type: REFERENCE_TYPE.ACTIONS,
				name: data.name,
				category: REFERENCE_TYPE.ACTIONS,
				child: [ mapperChild(data.opts) ]
			};

			return component;
		}
	}

	private getEvent(name: string): ReferenceEvent {
		const data = CoreComponentEventNodeTypes[name];

		if (!data) {
			return null;
		}

		const component: ReferenceEvent = {
			label: data.label,
			type: REFERENCE_TYPE.EVENTS,
			name: data.name,
			category: REFERENCE_TYPE.EVENTS,
			child: [ mapperChild(data.opts) ]
		};

		return component;
	}

	private getComponentsNav(): ReferenceComponent {
		const registredComponents: IComponentDefRegistry = this.runtimeService.getComponentRegistry();
		const componentsData = registredComponents.getFilteredAllItems();

		const components: ReferenceComponent = {
			label: REFERENCE_TYPE.COMPONETS,
			child: this.sort(
				Object.keys(componentsData).map((item) => ({
					label: ComponentCategories[item].label,
					child: this.sort(
						Object.values(componentsData[item]).map((item) => ({
							label: item.label || null,
							name: item.name,
							type: REFERENCE_TYPE.COMPONETS,
							hidden: item?.hidden || false,
							category: REFERENCE_TYPE.COMPONETS
						}))
					)
				}))
			)
		};

		return components;
	}

	private getDataSourceNav(): ReferenceDataSource {
		const registredDataSource: IDataSourceDefRegistry = this.runtimeService.getDataSourceRegistry();
		const dataSource = registredDataSource["items"];

		const components: ReferenceDataSource = {
			label: REFERENCE_TYPE.DATASOURCE,
			child: this.sort(
				Object.keys(dataSource).map((item) => ({
					label: dataSource[item].label,
					type: REFERENCE_TYPE.DATASOURCE,
					category: REFERENCE_TYPE.DATASOURCE,
					description: dataSource[item].description,
					name: dataSource[item].name
				}))
			)
		};

		return components;
	}

	private getFunctionsNav(): ReferenceFunction {
		const registredFunctions: IFunctionDefRegistry = this.runtimeService.getFunctionRegistry();
		const functionsData = registredFunctions.getFilteredAllItems();

		const functions: ReferenceFunction = {
			label: REFERENCE_TYPE.FUNCTIONS,
			child: this.sort(
				Object.keys(functionsData).map((item) => ({
					label: item.charAt(0).toUpperCase() + item.slice(1),
					child: this.sort(
						Object.keys(functionsData[item]).map((itemChild) => ({
							label: functionsData[item][itemChild].label,
							name: functionsData[item][itemChild].name,
							category: REFERENCE_TYPE.FUNCTIONS,
							type: REFERENCE_TYPE.FUNCTIONS
						}))
					)
				}))
			)
		};

		return functions;
	}

	private getIntegrationsNav(): ReferenceIntegration {
		const integrationsData: IIntegrationType[] = this.integrationService.getIntegrationTypes();

		const integrations: ReferenceIntegration = {
			label: REFERENCE_TYPE.INTEGRATIONS,
			child: this.sort(
				integrationsData.map((item) => ({
					label: item.label,
					category: REFERENCE_TYPE.INTEGRATIONS,
					type: REFERENCE_TYPE.INTEGRATIONS,
					hidden: item.hidden,
					name: item.integrationType,
					description: item.description,
					functions: this.sort(
						item.functions.map((itemChild) => ({
							category: item.label,
							label: itemChild.label,
							type: REFERENCE_TYPE.INTEGRATIONS,
							parrentType: item.integrationType,
							name: itemChild.functionName
						}))
					),
					child: mapperChild(item.optionsSchema)
				}))
			)
		};

		return integrations;
	}

	private getActionsNav(): ReferenceAction {
		const actionsNodesData = BlueprintActionNodeTypes;

		const actionNodesChilds = this.sort(
			Object.keys(actionsNodesData).map((item) => ({
				label: actionsNodesData[item].label,
				type: REFERENCE_TYPE.ACTIONS,
				name: actionsNodesData[item].name,
				category: REFERENCE_TYPE.ACTIONS
			}))
		);

		const actions: ReferenceAction = {
			label: REFERENCE_TYPE.ACTIONS,
			child: [
				{
					label: "Config",
					type: REFERENCE_TYPE.ACTIONS,
					name: "ActionConfig",
					category: REFERENCE_TYPE.ACTIONS
				},
				...actionNodesChilds
			]
		};

		return actions;
	}

	private getEventsNav(): ReferenceEvent {
		const eventData = CoreComponentEventNodeTypes;

		const events = {
			label: REFERENCE_TYPE.EVENTS,
			child: Object.keys(eventData).map((item) => ({
				label: eventData[item].label,
				type: REFERENCE_TYPE.EVENTS,
				name: eventData[item].name,
				category: REFERENCE_TYPE.EVENTS
			}))
		};

		return events;
	}

	private combineNav() {
		const components = this.searchComponents || this.components;
		const functions = this.searchFunctions || this.functions;
		const integrations = this.searchIntegrations || this.integrations;
		const actions = this.searchActions || this.actions;
		const events = this.searchEvents || this.events;
		const dataSource = this.searchDataSource || this.dataSource;

		const combineData: TReferenceData[] = [
			components,
			functions,
			integrations,
			actions,
			events,
			dataSource
		];

		this.nav = combineData;
	}

	public getNav(): TReferenceData[] {
		return this.nav;
	}

	public getContent(): TReferenceData {
		return this.content;
	}

	public updateContent = (selected) => {
		let dataUpdate;

		if (selected.type === REFERENCE_TYPE.COMPONETS) {
			dataUpdate = this.getComponent(selected.name);
		} else if (selected.type === REFERENCE_TYPE.FUNCTIONS) {
			dataUpdate = this.getFunction(selected.name);
		} else if (selected.type === REFERENCE_TYPE.INTEGRATIONS) {
			if (selected.category !== REFERENCE_TYPE.INTEGRATIONS) {
				dataUpdate = this.getIntegrationFunction(
					selected.parrentType,
					selected.name,
					selected.category
				);
			} else {
				dataUpdate = this.getIntegration(selected.name);
			}
		} else if (selected.type === REFERENCE_TYPE.ACTIONS) {
			try {
				dataUpdate = this.getAction(selected.name);
			} catch {
				dataUpdate = this.getEvent(selected.name);
			}
		} else if (selected.type === REFERENCE_TYPE.EVENTS) {
			dataUpdate = this.getEvent(selected.name);
		} else if (selected.type === REFERENCE_TYPE.DATASOURCE) {
			dataUpdate = this.getDataSource(selected.name);
		}

		this.content = dataUpdate;
	};

	private sort(elements) {
		const sortForLabels = (x, y) => {
			if (x.label < y.label) return -1;
			if (x.label > y.label) return 1;
			return 0;
		};

		elements.sort(sortForLabels);

		return elements;
	}

	public searchReference(value: string) {
		this.components = this.getComponentsNav();
		this.functions = this.getFunctionsNav();
		this.integrations = this.getIntegrationsNav();
		this.actions = this.getActionsNav();

		const searchInCategory = (elementsChild) => {
			const resultElements = elementsChild.map((categoryItem) => {
				const child = categoryItem.child.map((childItem) => {
					if (childItem.label.toLowerCase().indexOf(value) >= 0) {
						return childItem;
					}
				});

				const results = child.filter((element) => element !== undefined);
				categoryItem.child = results;
				return categoryItem;
			});

			return resultElements;
		};

		const search = (elementsChild) => {
			const child = elementsChild.map((childItem) => {
				if (childItem.label.toLowerCase().indexOf(value) >= 0) {
					return childItem;
				}
			});

			const results = child.filter((element) => element !== undefined);

			return results;
		};

		this.searchComponents.child = searchInCategory(this.components.child);
		this.searchFunctions.child = searchInCategory(this.functions.child);
		this.searchIntegrations.child = search(this.integrations.child);
		this.searchActions.child = search(this.actions.child);

		this.combineNav();
	}

	public getLayout(): LayoutState {
		return this.layout;
	}

	public getViewInfo(): IViewInfo {
		return {
			label: "Reference Guide",
			icon: "mdi/bookshelf",
			canClose: true,
			isModified: false,
			renderWhenNotActive: true
		};
	}

	public dispose(): void {
		// console.log()
	}
}
